import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, ArrowRight } from 'lucide-react';

/* ── Team roster ──
   One object per profile card. To update the team, edit this array only —
   the card markup below is generic and needs no changes.
     name     → displayed name
     role     → job title shown in the coloured pill
     initials → avatar text (2–3 characters works best)
     bio      → 1–2 sentence summary
     tags     → skill/focus chips (keep to 2–4 for tidy wrapping)
     accent   → pill + glow colour   grad → avatar gradient */
const team = [
  {
    name: 'A.S. Jayawickrama',
    role: 'Founder & CEO',
    initials: 'AJ',
    bio: 'Founded AlteraCode in March 2026 to build technology that solves real problems. Sets company direction and leads engineering strategy across every project.',
    tags: ['Software Engineering', 'AI', 'Strategy'],
    accent: '#00c8ff',
    grad: 'var(--grad-main)',
  },
  {
    name: 'W.A.R. Jayasinghe',
    role: 'CTO',
    initials: 'WJ',
    bio: 'Owns the technical architecture behind AlteraCode products, from AI systems to production infrastructure, and keeps the engineering standard high.',
    tags: ['AI Engineering', 'Architecture', 'Cloud'],
    accent: '#a855f7',
    grad: 'var(--grad-edu)',
  },
  {
    name: 'P.A.D. Anuja Mewan',
    role: 'CMO',
    initials: 'AM',
    bio: 'Leads brand, marketing and client growth — telling the AlteraCode story and connecting the team with the partners it builds for.',
    tags: ['Marketing', 'Brand', 'Growth'],
    accent: '#e040fb',
    grad: 'linear-gradient(135deg, #e040fb, #4488ff)',
  },
  {
    name: 'A.S.R. Fernando',
    role: 'CPO',
    initials: 'AF',
    bio: 'Shapes what we build and why. Turns client needs into clear product requirements and guides design and delivery from concept to launch.',
    tags: ['Product', 'UI/UX', 'Research'],
    accent: '#00dc82',
    grad: 'var(--grad-green)',
  },
  {
    name: 'D.M.M.N. Vidmanthi',
    role: 'COO',
    initials: 'DV',
    bio: 'Runs day-to-day operations, task tracking and delivery scheduling so every project ships on time and to spec.',
    tags: ['Operations', 'Data Science', 'Delivery'],
    accent: '#00c8ff',
    grad: 'linear-gradient(135deg, #00dc82, #4488ff)',
  },
  {
    name: '13+ AlteraCode Developers',
    role: 'Engineering Team',
    initials: 'AC',
    bio: 'The engineers behind every build — a hand-picked team spanning full stack development, artificial intelligence, cybersecurity and cloud.',
    tags: ['Full Stack', 'AI', 'Cybersecurity', 'Cloud'],
    accent: '#a855f7',
    grad: 'var(--grad-main)',
  },
];

const values = [
  { Icon: Target, title: 'Innovation First', desc: 'Our 13+ developers constantly explore new technologies to build solutions that are ahead of the curve.', color: 'var(--ac-cyan)' },
  { Icon: Eye, title: 'Technical Excellence', desc: 'We maintain the highest coding standards — every project is built to scale, perform and last.', color: 'var(--ac-purple)' },
  { Icon: Heart, title: 'Built for the World', desc: 'Our developers are committed to transforming the global tech landscape, delivering solutions that make an impact everywhere.', color: 'var(--ac-pink)' },
];

const timeline = [
  { 
    year: 'Mar 2026', 
    title: 'AlteraCode Founded', 
    desc: 'AlteraCode was founded with a bold vision — to deliver real-world technology solutions and transform lives through code.' 
  },
  { 
    year: 'Apr 2026', 
    title: 'First Project Begins', 
    desc: 'Our first real-world client project officially kicked off, marking AlteraCode\'s entry into professional software development.' 
  },
  { 
    year: 'May 2026', 
    title: 'Team Expansion', 
    desc: 'Second wave of developer recruitment begins — growing our team of talented engineers and tech specialists.' 
  },
  { 
    year: 'Jul 2026', 
    title: 'AlteraCode Scholar Program', 
    desc: 'Launching the AlteraCode Scholar Program — empowering school leavers to start their tech careers before university.' 
  },
];

export default function About() {
  return (
    <div style={{ paddingTop: 68 }}>

      {/* Hero */}
      <section style={{
        padding: '5rem 0 4rem',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(0,200,255,0.07) 0%, transparent 60%), var(--ac-dark)',
        textAlign: 'center',
      }}>
        <div className="container">
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <img
              src="/logo.png"
              alt="AlteraCode"
              style={{ height: 80, width: 'auto', objectFit: 'contain', opacity: 0.95 }}
            />
          </div>
          <div className="badge badge-cyan" style={{ marginBottom: '1.2rem' }}>About AlteraCode</div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.15, marginBottom: '1.2rem' }}>
            We Build. We Teach.<br /><span className="grad-text">We Transform.</span>
          </h1>
          <p style={{ fontSize: 17, color: 'var(--ac-muted)', maxWidth: 600, margin: '0 auto 2rem', lineHeight: 1.8 }}>
            AlteraCode is a global IT company powered by 13+ skilled developers delivering innovative real-world technology solutions — and shaping the next generation of tech professionals through our Scholar Program.
          </p>
        </div>
      </section>

      <div className="glow-divider" />

      {/* Mission / Vision */}
      <section className="section">
        <div className="container">
          <div className="grid-3">
            {values.map((v, i) => (
              <div key={i} className="card" style={{ textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: 'var(--radius-lg)', background: v.color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.2rem', border: `1px solid ${v.color}25` }}>
                  <v.Icon size={24} color={v.color} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 8 }}>{v.title}</h3>
                <p style={{ fontSize: 14, color: 'var(--ac-muted)', lineHeight: 1.7 }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section" style={{ background: 'var(--ac-navy)' }}>
        <div className="container">
          <div className="layout-2col-even">
            <div>
              <div className="badge badge-purple" style={{ marginBottom: '1.2rem' }}>Our Story</div>
              <h2 style={{ fontSize: 36, fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.2, marginBottom: '1.2rem' }}>
                Born from a <span className="grad-text-edu">Passion for Tech</span>
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ac-muted)', lineHeight: 1.8, marginBottom: '1rem' }}>
                AlteraCode was founded in March 2026 by A. Sithika Jayawickrama with a clear mission — to build innovative technology solutions that solve real-world problems. What started as a bold idea quickly grew into a team of 13+ passionate developers, engineers and tech specialists.
              </p>
              <p style={{ fontSize: 15, color: 'var(--ac-muted)', lineHeight: 1.8, marginBottom: '1rem' }}>
                Our development team brings together expertise across Full Stack Development, Artificial Intelligence, Cybersecurity, Cloud Computing and Data Engineering. Every developer at AlteraCode is handpicked for their technical skill, problem-solving ability and passion for innovation.
              </p>
              <p style={{ fontSize: 15, color: 'var(--ac-muted)', lineHeight: 1.8, marginBottom: '1rem' }}>
                We don't just write code — we architect solutions. From the first line of code to final deployment, our developers work collaboratively using modern technologies and agile methodologies to deliver high-quality software products.
              </p>
              <p style={{ fontSize: 15, color: 'var(--ac-muted)', lineHeight: 1.8 }}>
                Alongside our project work, we are deeply committed to growing the global tech talent pipeline through our AlteraCode Scholar Program — giving the next generation of developers the skills and confidence to succeed in the industry before they even enter university.
              </p>
            </div>
            {/* Timeline */}
            <div className="timeline">
              {timeline.map((t, i) => (
                <div key={i} className="timeline__item">
                  <span className="timeline__dot" />
                  <span className="timeline__year">{t.year}</span>
                  <h4 className="timeline__title">{t.title}</h4>
                  <p className="timeline__desc">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="badge badge-green" style={{ marginBottom: '1rem' }}>The People</div>
            <h2>Meet Our Team</h2>
            <p>"A passionate team of 13+ developers, engineers and educators united by one goal — transforming lives through technology, globally."</p>
          </div>
          <div className="grid-3">
            {team.map((m, i) => (
              <article key={m.name} className="team-card" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="team-card__glow" style={{ background: m.accent }} />
                <div className="team-card__avatar" style={{ background: m.grad }}>{m.initials}</div>
                <h3 className="team-card__name">{m.name}</h3>
                <span className="team-card__role" style={{
                  color: m.accent,
                  background: m.accent + '14',
                  border: `1px solid ${m.accent}40`,
                }}>{m.role}</span>
                <p className="team-card__bio">{m.bio}</p>
                <div className="team-card__tags">
                  {m.tags.map(t => <span key={t} className="team-card__tag">{t}</span>)}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-sm">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 32, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '1rem' }}>Ready to work with us?</h2>
          <p style={{ color: 'var(--ac-muted)', marginBottom: '2rem', fontSize: 16 }}>Let's build something great together.</p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary btn-lg">Get in Touch <ArrowRight size={18} /></Link>
            <Link to="/educenter" className="btn btn-edu btn-lg">Join EduCenter</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
